/**
 * Découpe `seminaire-CONDUCTEUR.md` en sections indexées par numéro de slide.
 *
 * C'EST CE QUI REND LE PROMPTEUR POSSIBLE, et ça n'aurait pas marché avant le
 * 07/09 : le conducteur numérotait ses sections d'une façon, le deck d'une
 * autre, et le support d'une troisième. Les titres de section sont désormais
 * alignés sur les `num:` de `seminaireSlidesData.js`, donc une simple lecture
 * des titres suffit à relier ce qui se dit à ce qui s'affiche.
 *
 * ⚠️ SI LA NUMÉROTATION REDÉRIVE, LE PROMPTEUR SUIVRA LA MAUVAISE SECTION —
 * silencieusement. D'où `verifierCouverture()` plus bas et les
 * `avertissements` rendus par `parseConducteur()`, qui donnent de quoi le
 * détecter au lieu de le découvrir sur scène.
 *
 * Titres reconnus — le séparateur peut être « — », « – », « : » ou « - » :
 *   ## SLIDE 12 — Titre · *0:12 → 0:14*
 *   ## SLIDES 5 → 15 — L'usine · *8:15 → 22:30*
 *   ### SLIDE 6 — La référence · *bilan de la manche I*
 */

const RE_TITRE = /^(#{2,3})\s+SLIDES?\s+(\d+)(?:\s*(?:→|->)\s*(\d+))?\s*(?:[—–:-]\s*(.*))?$/;

/**
 * ⚠️ POURQUOI LE SÉPARATEUR EST TOLÉRANT (14/09). Il ne l'était pas : seul le
 * cadratin « — » était accepté. Deux titres du conducteur étaient écrits
 * autrement — « ### SLIDES 12 : la phrase unique » et « ## SLIDES 17 - Changement
 * de destinataire » — et ces deux sections n'existaient tout simplement pas
 * pour le prompteur. En séance, passer de la slide 11 à la 12 faisait remonter
 * l'affichage à la slide 5 : faute de section propre, la 12 retombait sur le
 * chapitre qui la contient, « ## SLIDES 5 → 15 — L'usine », qui commence à la 5.
 *
 * Le conducteur est écrit à la main, entre deux répétitions. Un deux-points ou
 * un trait d'union à la place du cadratin ne doit pas coûter une section sur
 * scène : on accepte les quatre, et `sectionsIllisibles()` signale ce qui
 * ressemble à un titre sans en être un.
 */
const RE_TITRE_APPROCHANT = /^#{2,3}\s+SLIDES?\s/i;

/* ─────────────────────────── le rythme de parole ─────────────────────────
 * LE CURSEUR ET LA FRISE AVANCENT AU MOT DIT, PAS AU PIXEL (14/09). Un
 * paragraphe de trente mots prend dix fois plus de temps qu'un « *Temps.* »,
 * et un tableau ne se dit pas du tout : une répartition à la hauteur affichée
 * ferait courir le curseur sur les indications et traîner sur le texte.
 *
 * 150 mots par minute, c'est le débit d'une conférence posée. La valeur
 * absolue compte peu : elle ne sert qu'à convertir un silence en mots, et à
 * donner une durée aux slides qui n'en ont aucune. Partout ailleurs, le
 * curseur se cale sur la durée PRÉVUE de la slide, quel que soit ce débit. */
export const MOTS_PAR_SECONDE = 2.5;
/** « *Temps.* », « *Laisser lire.* » : un silence sans durée écrite. */
const SILENCE_PAR_DEFAUT = 3;
/** Une slide presque muette (une vidéo, une phrase seule) garde un minimum de
    temps dans la frise : sans plancher, elle tomberait à zéro seconde. */
const PLANCHER_SECONDES = 10;

/* ─────────────────────────── l'étirement du minutage ─────────────────────
 * TOUS LES HORAIRES DU CONDUCTEUR SONT MULTIPLIÉS PAR CE FACTEUR (29/09). Le
 * conducteur est minuté sur 50 min 30, au débit de la répétition ; le créneau
 * en fait plus d'une heure, et le curseur allait trop vite. Plutôt que de
 * réécrire les 35 horaires, on étire la frise d'un bloc : curseur, fin de
 * slide et badge d'écart ralentissent ensemble et restent cohérents.
 * 1 = le conducteur tel qu'il est écrit. 1,15 → 50:30 devient ~58 min. */
export const ETIREMENT_MINUTAGE = 1.15;

/** « *0:38:45 → 0:41*  » ou « *8:15 → 22:30* » → secondes depuis le début. */
function enSecondes(horodate) {
    const p = horodate.split(':').map(Number);
    if (p.some(Number.isNaN)) return null;
    if (p.length === 3) return p[0] * 3600 + p[1] * 60 + p[2];
    if (p.length === 2) return p[0] * 60 + p[1];
    return null;
}

function extraireMinutage(reste) {
    // Le minutage est le dernier segment en italique du titre, et il contient
    // une flèche. Le repère de manche (« *bilan de la manche I* ») n'en a pas :
    // c'est ce qui les distingue, et pas leur position.
    const m = /\*([^*]*(?:→|->)[^*]*)\*/.exec(reste || '');
    // Pas de minutage : il reste peut-être un repère en italique — « *passage 1* »,
    // « *bilan de la manche I* ». Il fait partie du titre, mais ses astérisques
    // n'ont rien à faire dans un bandeau : on les retire.
    if (!m) {
        return {
            titre: (reste || '').replace(/\*/g, '').replace(/\s*·\s*$/, '').trim(),
            debut: null,
            fin: null
        };
    }
    const [de, a] = m[1].split(/→|->/).map((s) => s.trim());
    // ⚠️ LES DEUX PEUVENT COHABITER : « · *bilan de la manche I* · *10:00 →
    // 10:48* ». C'est même le seul moyen de donner un horaire à une slide qui
    // porte déjà un repère de manche, sans perdre le repère. Les astérisques
    // du repère se retirent donc ici aussi, sinon ils s'affichent tels quels
    // dans le bandeau.
    return {
        titre: (reste.slice(0, m.index) || '').replace(/\*/g, '').replace(/\s*·\s*$/, '').trim(),
        debut: enSecondes(de),
        fin: enSecondes(a)
    };
}

/* ───────────────────────────── les blocs ─────────────────────────────── */

const RE_PUCE = /^\s*(?:[-*]|\d+\.)\s+/;

/**
 * Une note de marge : `:: Goulot · 5 étapes`, seule sur sa ligne.
 *
 * Ce sont les repères de la colonne de gauche du prompteur — deux à cinq mots,
 * ce qu'on attrape d'un coup d'œil quand on a perdu le fil. Elles ne se disent
 * pas : elles ne sont pas des blocs, ne pèsent rien, et ne décalent donc ni le
 * curseur ni la frise. Chacune est rattachée au bloc qui la suit, pour que la
 * marge suive la lecture.
 */
const RE_MARGE = /^::\s+(.+)$/;

/**
 * Découpe le corps d'une section en blocs : paragraphe, liste, indication,
 * tableau, séparateur.
 *
 * ⚠️ C'EST LE SEUL DÉCOUPAGE DU CONDUCTEUR, et il est ici plutôt que dans
 * `RenduConducteur` pour une raison précise : le curseur désigne un bloc par
 * son INDICE. Si le rendu et le calcul des poids découpaient chacun de leur
 * côté, la moindre divergence décalerait le curseur d'un paragraphe — sans
 * erreur, juste faux.
 */
export function decouperBlocs(corps, marge = null) {
    const lignes = (corps || '').split('\n');
    const blocs = [];
    let i = 0;

    while (i < lignes.length) {
        const l = lignes[i];

        if (l.trim() === '') { i++; continue; }

        const note = RE_MARGE.exec(l.trim());
        if (note) {
            if (marge) marge.push({ texte: note[1].trim(), bloc: blocs.length });
            i++;
            continue;
        }

        // Un titre qui n'est pas un titre de slide : « Points à dire, jamais à
        // projeter », « À ajuster avant le jour J », en fin de conducteur.
        if (RE_TITRE_LIBRE.test(l)) {
            blocs.push({ type: 'titre', texte: l.replace(/^#+\s*/, '') });
            i++;
            continue;
        }

        if (l.trim() === '---') {
            blocs.push({ type: 'separateur' });
            i++;
            continue;
        }

        if (l.startsWith('>')) {
            const bloc = [];
            while (i < lignes.length && (lignes[i].startsWith('>') || (bloc.length && lignes[i].trim() === ''))) {
                if (lignes[i].trim() === '' && !lignes[i + 1]?.startsWith('>')) break;
                bloc.push(lignes[i]);
                i++;
            }
            blocs.push({ type: 'indication', lignes: bloc });
            continue;
        }

        if (l.trim().startsWith('|')) {
            const bloc = [];
            while (i < lignes.length && lignes[i].trim().startsWith('|')) { bloc.push(lignes[i]); i++; }
            blocs.push({ type: 'tableau', lignes: bloc });
            continue;
        }

        if (RE_PUCE.test(l)) {
            const items = [];
            while (i < lignes.length && RE_PUCE.test(lignes[i])) {
                items.push(lignes[i].replace(RE_PUCE, ''));
                i++;
            }
            blocs.push({ type: 'liste', items });
            continue;
        }

        // Paragraphe : CE QUI SE DIT. Les lignes contiguës sont recollées —
        // le markdown est enroulé à 80 colonnes, la scène n'en a que faire.
        const bloc = [];
        while (
            i < lignes.length &&
            lignes[i].trim() !== '' &&
            !lignes[i].startsWith('>') &&
            !lignes[i].trim().startsWith('|') &&
            lignes[i].trim() !== '---' &&
            !RE_MARGE.test(lignes[i].trim()) &&
            !RE_PUCE.test(lignes[i]) &&
            !RE_TITRE_LIBRE.test(lignes[i])
        ) {
            bloc.push(lignes[i].trim());
            i++;
        }
        blocs.push({ type: 'paragraphe', texte: bloc.join(' ') });
    }

    /* ⚠️ UN TITRE LIBRE OUVRE UNE ANNEXE. La dernière section du conducteur
       traîne derrière elle le tableau « Points à dire » et la liste « À
       ajuster avant le jour J ». Elles restent affichées, mais ne se disent
       pas : comptées comme du texte, elles donnaient 347 mots aux 30 secondes
       de la slide 36, et le curseur aurait couru dans la liste des choses à
       vérifier pendant qu'on dit merci. */
    const k = blocs.findIndex((b) => b.type === 'titre');
    if (k >= 0) for (let j = k; j < blocs.length; j++) blocs[j].annexe = true;

    return blocs;
}

/** Un titre markdown qui n'est pas un titre de slide — ceux-là sont déjà
    consommés par `parseConducteur` et n'arrivent jamais jusqu'ici. */
const RE_TITRE_LIBRE = /^#{1,6}\s/;

/** Le texte d'une indication, sans ses chevrons. */
export function texteIndication(lignes) {
    return lignes.map((l) => l.replace(/^>\s?/, '')).join('\n');
}

/** Les mots d'un texte, sans compter la ponctuation isolée (« — », « « »). */
function compterMots(texte) {
    return texte
        .replace(/[*`]/g, ' ')
        .split(/\s+/)
        .filter((m) => /[0-9A-Za-zÀ-ÖØ-öø-ÿŒœ]/.test(m)).length;
}

/**
 * Le silence qu'impose une indication, en secondes.
 *
 * Une indication se FAIT, elle ne se dit pas : elle ne coûte rien, sauf
 * quand elle impose un silence. Celles-là coûtent leur durée écrite
 * (« 3 secondes de silence », « les 30 à 40 secondes » → 35), et à défaut
 * une pause courte (« *Temps.* », « *Laisser lire.* »). Sans ça, la slide 33
 * — dont le cœur est un silence de 35 secondes — verrait son curseur filer
 * à la ligne suivante pendant qu'on se tait.
 */
export function secondesDeSilence(texte) {
    const t = (texte || '').replace(/\*/g, '');
    const m = /(\d+)(?:\s*(?:à|-|–)\s*(\d+))?\s*secondes?/i.exec(t);
    if (m) return m[2] ? (Number(m[1]) + Number(m[2])) / 2 : Number(m[1]);
    if (/\b(?:temps|silence|laisser)\b/i.test(t)) return SILENCE_PAR_DEFAUT;
    return 0;
}

/** Le poids d'un bloc, en mots dits — ou en mots équivalents à son silence. */
export function poidsBloc(bloc) {
    if (bloc.annexe) return 0;
    switch (bloc.type) {
        case 'paragraphe': return compterMots(bloc.texte);
        case 'liste': return bloc.items.reduce((n, it) => n + compterMots(it), 0);
        case 'indication': return secondesDeSilence(texteIndication(bloc.lignes)) * MOTS_PAR_SECONDE;
        default: return 0;
    }
}

/**
 * Où commence chaque slide À L'INTÉRIEUR d'une section, en poids.
 *
 * Utile aux sections qui couvrent plusieurs slides (« SLIDES 29 → 30 ») : sans
 * ça, passer de la 29 à la 30 renverrait le curseur au début de la section,
 * c'est-à-dire au texte qu'on vient de dire. Le conducteur marque déjà la
 * bascule par une indication qui COMMENCE par le numéro — « *Slide 30. C'est
 * la seule slide…* » — et c'est elle qui sert de borne. À défaut de repère,
 * la section est partagée à proportion.
 */
const RE_REPERE_SLIDE = /^\W*slides?\s+(\d+)\b/i;

function bornesDesSlides(s) {
    const connues = { [s.de]: 0, [s.a + 1]: s.total };
    s.blocs.forEach((b, i) => {
        if (b.type !== 'indication') return;
        const m = RE_REPERE_SLIDE.exec(texteIndication(b.lignes));
        const n = m ? Number(m[1]) : null;
        if (n > s.de && n <= s.a && connues[n] == null) connues[n] = s.cumuls[i];
    });
    const cles = Object.keys(connues).map(Number).sort((x, y) => x - y);
    const bornes = {};
    for (let n = s.de; n <= s.a + 1; n++) {
        if (connues[n] != null) {
            bornes[n] = connues[n];
        } else {
            const kA = [...cles].reverse().find((k) => k < n);
            const kB = cles.find((k) => k > n);
            bornes[n] = connues[kA] + ((connues[kB] - connues[kA]) * (n - kA)) / (kB - kA);
        }
        // Un repère écrit dans le désordre ne doit pas donner un segment négatif.
        if (n > s.de) bornes[n] = Math.max(bornes[n], bornes[n - 1]);
    }
    return bornes;
}

/** Le segment d'une section qui revient à la slide `n`, en poids. */
export function segmentDe(section, n) {
    return { debut: section.bornes[n] ?? 0, fin: section.bornes[n + 1] ?? section.total };
}

/** `parSlide[n]` = la section la PLUS PRÉCISE couvrant la slide n. */
function indexerParSlide(sections) {
    const parSlide = {};
    for (const s of sections) {
        for (let n = s.de; n <= s.a; n++) {
            const dejaLa = parSlide[n];
            // Plus le niveau est grand, plus la section est précise.
            if (!dejaLa || s.niveau >= dejaLa.niveau) parSlide[n] = s;
        }
    }
    return parSlide;
}

/**
 * @returns {{sections: Array, parSlide: Object, avertissements: Array}}
 *   `parSlide[n]` = la section la PLUS PRÉCISE couvrant la slide n. Une
 *   sous-section (`###`) l'emporte toujours sur le chapitre (`##`) qui la
 *   contient : sur la slide 6, on veut « La référence », pas « L'usine ».
 *
 *   Chaque section porte ses `blocs`, leur `poids` (en mots), les `cumuls`
 *   (poids avant chaque bloc), le `total`, et les `bornes` de ses slides.
 */
export function parseConducteur(markdown) {
    const lignes = (markdown || '').split('\n');
    const sections = [];
    const avertissements = [];
    let courante = null;

    for (const ligne of lignes) {
        const m = RE_TITRE.exec(ligne.trim());
        if (!m && RE_TITRE_APPROCHANT.test(ligne.trim())) {
            // Ça ressemble à un titre de slide et ça n'en est pas un. La
            // ligne reste dans le corps de la section en cours — rien ne
            // disparaît — mais le prompteur le dit, au lieu de suivre
            // silencieusement la mauvaise section.
            avertissements.push(ligne.trim());
        }
        if (m) {
            if (courante) sections.push(courante);
            const { titre, debut, fin } = extraireMinutage(m[4]);
            courante = {
                niveau: m[1].length,          // 2 = chapitre, 3 = sous-section
                de: Number(m[2]),
                a: Number(m[3] || m[2]),
                titre,
                debut,
                fin,
                lignes: []
            };
        } else if (courante) {
            courante.lignes.push(ligne);
        }
        // Avant le premier titre : en-tête et règles de scène, hors prompteur.
    }
    if (courante) sections.push(courante);

    for (const s of sections) {
        // Les commentaires de provenance ne se lisent pas sur scène.
        s.corps = s.lignes.join('\n').replace(/<!--[\s\S]*?-->/g, '').trim();
        delete s.lignes;
        s.marge = [];
        s.blocs = decouperBlocs(s.corps, s.marge);
        s.poids = s.blocs.map(poidsBloc);
        s.cumuls = [];
        let t = 0;
        for (const p of s.poids) { s.cumuls.push(t); t += p; }
        s.total = t;
        s.bornes = bornesDesSlides(s);
    }

    return { sections, parSlide: indexerParSlide(sections), avertissements };
}

/**
 * L'HORAIRE DE FIN ATTENDU DE CHAQUE SLIDE, y compris celles dont la section
 * n'en porte aucun.
 *
 * ⚠️ POURQUOI UNE FIN ET NON UN DÉBUT (09/09). Le prompteur affichait l'heure à
 * laquelle la slide aurait dû COMMENCER. Sur scène c'est une information morte :
 * quand on la lit, on est déjà dessus depuis un moment, et elle ne dit rien de
 * ce qu'il reste. La question qu'on se pose en parlant est « dans combien de
 * temps dois-je être passé à la suivante ». Le repère est donc la fin, et
 * l'écart devient un compte à rebours : négatif tant qu'il reste du temps sur
 * la slide, positif dès qu'on déborde.
 *
 * La fin d'une slide est le début de la suivante — d'où l'appel à `debut(n + 1)`.
 *
 * ⚠️ POURQUOI LA FRISE EXISTE. Le prompteur lisait `section.debut`, et
 * `parSlide` retient toujours la section la PLUS PRÉCISE : sur la slide 5,
 * c'est `### SLIDE 5 — Trois cuves · *passage 1*`, qui n'a pas d'horaire — le
 * minutage du chapitre `## SLIDES 5 → 15 · *8:15 → 22:30*` était donc jeté.
 * Résultat en séance : l'écart au minutage, la seule chose qui dit « tu es en
 * retard », disparaissait sans un mot de la slide 5 à la slide 15, puis de la
 * 18 à la 29. Soit vingt slides sur trente-six, c'est-à-dire pile la moitié du
 * talk où l'on peut encore rattraper quelque chose.
 *
 * LE PRINCIPE : on ne lit plus une section, on construit une FRISE. Chaque
 * minutage rencontré pose deux repères — un début sur sa première slide, une
 * fin sur celle qui suit sa dernière — et le temps qui sépare deux repères est
 * réparti entre les slides qu'ils encadrent. C'est aussi ce qui donne sa fin à
 * la DERNIÈRE slide du deck : le repère posé en `s.a + 1` par la dernière
 * section tombe hors du deck, mais `debut(n + 1)` va le chercher.
 *
 * ⚠️ LA RÉPARTITION SE FAIT AU TEXTE, PAS À PARTS ÉGALES (14/09). Seules douze
 * slides portent un horaire écrit ; toutes les autres étaient interpolées à
 * parts égales — 1:18 chacune de la 5 à la 15, 1:01 de la 17 à la 28. La
 * slide 8, quatre lignes, recevait autant que la 9, vingt. L'écart mentait sur
 * les plages où l'on peut encore rattraper quelque chose, et le curseur de
 * lecture, qui se règle sur ces durées, aurait couru sur la 8 et rampé sur la
 * 9. Chaque slide reçoit donc une part proportionnelle aux mots de son texte,
 * avec un plancher pour celles qui n'en ont presque pas.
 *
 * ⚠️ UN DÉBUT DÉCLARÉ BAT TOUJOURS LA FIN DE LA SECTION PRÉCÉDENTE, et ce n'est
 * pas un détail : l'ouverture finit à 7:20 alors que l'usine est annoncée à
 * 8:15. Ces ~1 min sont un coussin voulu (voir l'en-tête du conducteur), pas
 * une incohérence à lisser. En donnant la priorité au début déclaré, le
 * prompteur affiche l'avance en vert au lieu de la faire disparaître.
 *
 * Une valeur répartie n'est pas une mesure : `exact` dit laquelle est
 * laquelle, et le prompteur préfixe les approximations d'un « ~ ». Sans ça on
 * lirait « fin ~9:33 » avec la même confiance qu'un horaire chronométré.
 *
 * @param etirement facteur appliqué à tous les horaires écrits — voir
 *   `ETIREMENT_MINUTAGE` ; 1 = le conducteur tel quel.
 * @returns {{attendu: Object, exact: Object, debut: Object}} indexés par
 *   numéro de slide. `debut[n]` est l'heure où la slide n doit COMMENCER :
 *   son début déclaré s'il y en a un, sinon la fin de la slide d'avant.
 */
export function minutageParSlide(sections, nums, etirement = 1) {
    const reperes = new Map();     // numéro de slide → secondes
    const declare = new Set();     // slides dont le repère est un début déclaré
    const ordonnes = [...nums].sort((x, y) => x - y);

    for (const s of sections) {
        if (s.debut == null) continue;
        reperes.set(s.de, s.debut * etirement);
        declare.add(s.de);
    }
    for (const s of sections) {
        // La fin d'une section est le début de la slide suivante — sauf si
        // cette slide-là déclare déjà le sien.
        if (s.fin == null || declare.has(s.a + 1)) continue;
        reperes.set(s.a + 1, s.fin * etirement);
    }

    const bornes = [...reperes.entries()].sort((a, b) => a[0] - b[0]);
    if (!bornes.length) return { attendu: {}, exact: {}, debut: {} };

    const parSlide = indexerParSlide(sections);
    const plancher = PLANCHER_SECONDES * MOTS_PAR_SECONDE;
    /** Le poids d'une slide : les mots de SON segment, pas de toute la section. */
    const poidsSlide = (n) => {
        const s = parSlide[n];
        if (!s || !s.bornes) return plancher;
        const { debut, fin } = segmentDe(s, n);
        return Math.max(plancher, fin - debut);
    };

    /** Horaire de DÉBUT d'une slide quelconque — déclaré, ou réparti. */
    const debut = (n) => {
        if (reperes.has(n)) return { valeur: reperes.get(n), exact: true };
        const avant = [...bornes].reverse().find(([k]) => k < n);
        const apres = bornes.find(([k]) => k > n);
        // Hors de la frise : on n'invente pas de valeur, le badge se tait.
        if (!avant || !apres) return null;
        const [kA, vA] = avant;
        const [kB, vB] = apres;
        const encadrees = ordonnes.filter((k) => k >= kA && k < kB);
        const total = encadrees.reduce((t, k) => t + poidsSlide(k), 0);
        const deja = encadrees.filter((k) => k < n).reduce((t, k) => t + poidsSlide(k), 0);
        const part = total > 0 ? deja / total : (n - kA) / (kB - kA);
        return { valeur: vA + (vB - vA) * part, exact: false };
    };

    /* ⚠️ LA FIN DÉCLARÉE D'UNE SECTION BAT LE DÉBUT DE LA SUIVANTE, sur la
       dernière slide de cette section. Sans cette règle, la slide 4 — annoncée
       « *4:30 → 7:20* » — se voyait attribuer 8:15, l'heure d'entrée dans
       l'usine : elle avalait le coussin d'une minute qui sépare les deux
       chapitres, et le prompteur affichait une cible plus lâche que celle
       écrite dans le conducteur. Le coussin est un coussin, pas du temps de
       parole sur la slide 4.
       Une sous-section l'emporte sur le chapitre qui la contient, même
       convention que `parSlide`. */
    const finDeclaree = new Map();
    const niveauFin = new Map();
    for (const s of sections) {
        if (s.fin == null) continue;
        if (!finDeclaree.has(s.a) || s.niveau >= niveauFin.get(s.a)) {
            finDeclaree.set(s.a, s.fin * etirement);
            niveauFin.set(s.a, s.niveau);
        }
    }

    const attendu = {};
    const exact = {};
    for (const n of nums) {
        if (finDeclaree.has(n)) {
            attendu[n] = finDeclaree.get(n);
            exact[n] = true;
            continue;
        }
        const fin = debut(n + 1);
        if (!fin) continue;
        attendu[n] = fin.valeur;
        exact[n] = fin.exact;
    }

    /* Le début d'une slide sert à deux choses : « caler » le chrono, et donner
       au curseur de lecture la durée de la slide (fin − début). Un début
       déclaré l'emporte — c'est ce qui rend son coussin à la slide 4 au lieu
       de le compter dans la durée de la 5. On passe par la liste ordonnée des
       numéros plutôt que par `n - 1` : le deck a déjà eu des trous de
       numérotation. */
    const debutSlide = {};
    ordonnes.forEach((n, i) => {
        if (declare.has(n)) debutSlide[n] = reperes.get(n);
        else if (i > 0 && attendu[ordonnes[i - 1]] != null) debutSlide[n] = attendu[ordonnes[i - 1]];
        else {
            const d = debut(n);
            if (d) debutSlide[n] = d.valeur;
        }
    });

    return { attendu, exact, debut: debutSlide };
}

/**
 * OÙ DEVRAIT EN ÊTRE LA LECTURE, dans la section affichée.
 *
 * ⚠️ LE CURSEUR PART DE L'ARRIVÉE SUR LA SLIDE, PAS DU PLAN GLOBAL. Réglé sur
 * l'horaire absolu, il serait déjà en bas de chaque slide dès la première
 * minute de retard, et le défilement sauterait à la fin du texte pendant qu'on
 * en lit le début. Il part donc du haut de la slide — ou du paragraphe qu'on a
 * désigné en cliquant — et avance au rythme PRÉVU pour cette slide : la durée
 * de la slide rapportée aux mots de son segment. Le retard global, lui, reste
 * le travail du badge d'écart.
 *
 * @param section   la section affichée (`parSlide[n]`)
 * @param n         la slide affichée
 * @param depart    poids où le curseur a été ancré ; null = début de la slide
 * @param secondes  temps de chrono écoulé depuis l'ancrage
 * @param duree     durée prévue de la slide, en secondes (null = au débit moyen)
 * @returns {{bloc: number, fraction: number, depasse: boolean, enTete: boolean}|null}
 *   `bloc` est l'indice du bloc courant dans `section.blocs`, `fraction` la
 *   position dans ce bloc (0 → 1), `depasse` dit que la slide aurait dû être
 *   finie, `enTete` que le curseur est encore sur le premier bloc dit.
 */
export function positionCurseur(section, n, depart, secondes, duree) {
    if (!section || !section.total || !section.bornes) return null;
    const { debut, fin } = segmentDe(section, n);
    const longueur = fin - debut;
    if (longueur <= 0) return null;

    const rythme = longueur / (duree > 0 ? duree : longueur / MOTS_PAR_SECONDE);
    const origine = depart ?? debut;
    // Ancré au-delà de sa slide (on a cliqué plus bas), le curseur continue
    // jusqu'au bout de la section plutôt que de rester collé à l'origine.
    const borne = origine >= fin ? section.total : fin;
    const brut = origine + Math.max(0, secondes) * rythme;
    const depasse = brut >= borne;
    const pos = Math.min(brut, borne);

    // Les blocs pondérés se succèdent sans trou : le bloc courant est le
    // dernier qui commence avant la position. Les blocs sans poids (tableaux,
    // indications muettes) sont traversés instantanément.
    const cible = depasse ? borne - 1e-9 : pos;
    const { poids, cumuls } = section;
    let bloc = -1;
    for (let i = 0; i < poids.length; i++) {
        if (poids[i] > 0 && cumuls[i] <= cible) bloc = i;
    }
    if (bloc < 0) return null;

    return {
        bloc,
        fraction: Math.min(1, Math.max(0, (pos - cumuls[bloc]) / poids[bloc])),
        depasse,
        enTete: bloc === poids.findIndex((p) => p > 0)
    };
}

/**
 * Contrôle de cohérence, à afficher dans le prompteur plutôt qu'à découvrir
 * en salle : quelles slides du deck n'ont aucune section, et quelles sections
 * visent une slide qui n'existe pas.
 */
export function verifierCouverture(parSlide, slides) {
    const nums = slides.map((s) => s.num).filter(Boolean);
    const sansSection = nums.filter((n) => !parSlide[n]);
    const horsDeck = Object.keys(parSlide)
        .map(Number)
        .filter((n) => !nums.includes(n));
    return { sansSection, horsDeck };
}
